import * as Redux from 'redux';
import { SubmissionResponseData } from '../api/models/SubmissionResponseData';
import { GridSelection } from '../models';
import { SimManagementError } from '../models/Errors';
import { CancelSubmissionPayload, CompleteSubmissionPayload, SimSubmissionPayload } from '../models/payload/simSubmission';
import { BasicPageableSearchFilter, SimSubmissionPageableSearchFilter } from '../models/searchFilter';
import {
  LoadSimSubmissionsFailurePayload,
  LoadSimSubmissionsSuccessPayload,
  SimSubmissionsSuccessResponse
} from '../models/simSubmission';
import { SimSubmission } from '../models/transfer/input/SimSubmission';
import {
  CANCEL_SIM_SUBMISSION,
  CANCEL_SIM_SUBMISSION_FAILURE,
  CANCEL_SIM_SUBMISSION_SUCCESS,
  COMPLETE_SIM_SUBMISSION,
  COMPLETE_SIM_SUBMISSION_FAILURE,
  COMPLETE_SIM_SUBMISSION_SUCCESS,
  CHANGE_SIM_SUBMISSIONS_SELECTED,
  CLEAR_SELECTED_SIM_SUBMISSION,
  CLEAR_SIM_SUBMISSION,
  CLEAR_SIM_SUBMISSIONS,
  CREATE_SIM_SUBMISSION,
  CREATE_SIM_SUBMISSION_FAILURE,
  CREATE_SIM_SUBMISSION_SUCCESS,
  ENSURE_SELECTED_SIM_SUBMISSION,
  ENSURE_SIM_SUBMISSIONS,
  EXPORT_SIM_SUBMISSION,
  LOAD_SELECTED_SIM_SUBMISSION,
  LOAD_SELECTED_SIM_SUBMISSION_FAILURE,
  LOAD_SELECTED_SIM_SUBMISSION_SUCCESS,
  LOAD_SIM_SUBMISSIONS,
  LOAD_SIM_SUBMISSIONS_FAILURE,
  LOAD_SIM_SUBMISSIONS_SUCCESS
} from './actionTypes';
import { Action, ActionMeta, PromiseAction, RejectFunc, ResolveFunc } from './commonActions';

export function createSimSubmission(
  payload: SimSubmissionPayload,
  resolve?: ResolveFunc<string>,
  reject?: RejectFunc
): PromiseAction<SimSubmissionPayload, string> {
  return {
    type: CREATE_SIM_SUBMISSION,
    payload,
    resolve,
    reject
  };
}

export function createSimSubmissionFailure(error: SimManagementError): Action<SimManagementError> {
  return {
    type: CREATE_SIM_SUBMISSION_FAILURE,
    payload: error,
    error: true
  };
}

/**
 * Action which is dispatched when a submission has been successfully created.
 * @param geoFence An object represnting the saved geo-fence.
 */
export function createSimSubmissionSuccess(id: string): Action<string> {
  return {
    type: CREATE_SIM_SUBMISSION_SUCCESS,
    payload: id
  };
}

/**
 * Action which exports the selected submission to an Excel file.
 * @param ticketNumber The submission ticket number.
 */
export function exportSimSubmission(ticketNumber: string): Action<string> {
  return {
    type: EXPORT_SIM_SUBMISSION,
    payload: ticketNumber
  };
}

/**
 * Action which ensures that the selected submission is loaded and will fetch from server if it is not.
 * @param ticketNumber The submission ticket number.
 */
export function ensureSelectedSimSubmission(ticketNumber: string): Action<string> {
  return {
    type: ENSURE_SELECTED_SIM_SUBMISSION,
    payload: ticketNumber
  };
}

/**
 * Action which loads the selected submission from the server.
 * @param ticketNumber The submission ticket number.
 */
export function loadSelectedSimSubmission(ticketNumber: string): Action<string> {
  return {
    type: LOAD_SELECTED_SIM_SUBMISSION,
    payload: ticketNumber
  };
}

/**
 * Action which handles when the selected submission is successfully loaded.
 * @param submission The submission.
 */
export function loadSelectedSimSubmissionSuccess(submission: SimSubmission) {
  return {
    type: LOAD_SELECTED_SIM_SUBMISSION_SUCCESS,
    payload: submission
  };
}

/**
 * Action which handles when there is a failure to load the selected SIM submission.
 * @param ticketNumber The submission ticket number.
 * @param error The error.
 */
export function loadSelectedSimSubmissionFailure(
  ticketNumber: string,
  error: SimManagementError
): ActionMeta<SimManagementError, string> {
  return {
    error: true,
    type: LOAD_SELECTED_SIM_SUBMISSION_FAILURE,
    payload: error,
    meta: ticketNumber
  };
}

/**
 * An action that changes the selected submission.
 * @param payload The grid selection.
 */
export function changeSimSubmissionsSelected(payload: GridSelection<SimSubmission>) {
  return {
    type: CHANGE_SIM_SUBMISSIONS_SELECTED,
    payload
  };
}

/**
 * An action that clears the selected submission.
 */
export function clearSelectedSimSubmission(): Redux.Action {
  return {
    type: CLEAR_SELECTED_SIM_SUBMISSION
  };
}

/**
 * An action that clears the submissions.
 */
export function clearSimSubmissions(): Redux.Action {
  return {
    type: CLEAR_SIM_SUBMISSIONS
  };
}

/**
 * An action that ensures submissions are loaded from the server.
 * @param payload The search filter.
 */
export function ensureSimSubmissions(
  payload: SimSubmissionPageableSearchFilter
): Action<SimSubmissionPageableSearchFilter> {
  return {
    type: ENSURE_SIM_SUBMISSIONS,
    payload
  };
}
/**
 * An action that loads submissions from the server.
 * @param payload The search filter.
 */
export function loadSimSubmissions(
  payload: SimSubmissionPageableSearchFilter,
  resolve?: ResolveFunc<SimSubmissionsSuccessResponse>,
  reject?: RejectFunc
): PromiseAction<SimSubmissionPageableSearchFilter, SimSubmissionsSuccessResponse> {
  return {
    type: LOAD_SIM_SUBMISSIONS,
    payload,
    resolve,
    reject
  };
}

/**
 * An action dispatched when submissions are successfully loaded from the server.
 * @param payload
 */
export function loadSimSubmissionsSuccess(payload: LoadSimSubmissionsSuccessPayload) {
  return {
    type: LOAD_SIM_SUBMISSIONS_SUCCESS,
    payload
  };
}

/**
 * An action dispatched when submissions fail to be loaded from the server.
 * @param searchFilter
 * @param error
 */
export function loadSimSubmissionsFailure(
  searchFilter: SimSubmissionPageableSearchFilter,
  error: SimManagementError
): Action<LoadSimSubmissionsFailurePayload> {
  return {
    type: LOAD_SIM_SUBMISSIONS_FAILURE,
    payload: {
      searchFilter,
      error
    }
  };
}

/**
 * An action that cancels a SIM submission.
 * @param payload The payload.
 * @param resolve Optional function which will be called on success.
 * @param reject Optional function which will be called on failure.
 */
export function cancelSimSubmission(
  payload: CancelSubmissionPayload,
  resolve?: ResolveFunc<SubmissionResponseData>,
  reject?: RejectFunc
): PromiseAction<CancelSubmissionPayload, SubmissionResponseData> {
  return {
    type: CANCEL_SIM_SUBMISSION,
    payload,
    resolve,
    reject
  };
}

/**
 * An action dispatched when a submission cancellation is successfully requested on the server.
 * @param submission The submission whose cancellation was requested.
 */
export function cancelSimSubmissionSuccess(submission: SimSubmission): Action<SimSubmission> {
  return {
    type: CANCEL_SIM_SUBMISSION_SUCCESS,
    payload: submission
  };
}

/**
 * An action dispatched when there is a failure to request a submission cancellation on the server.
 * @param originalPayload The original payload for the cancel submission request.
 * @param error The error which caused the action.
 */
export function cancelSimSubmissionFailure(
  originalPayload: CancelSubmissionPayload,
  error: SimManagementError
): ActionMeta<SimManagementError, CancelSubmissionPayload> {
  return {
    error: true,
    type: CANCEL_SIM_SUBMISSION_FAILURE,
    payload: error,
    meta: originalPayload
  };
}

export function completeSimSubmission(
  payload: CompleteSubmissionPayload,
  resolve?: ResolveFunc<SubmissionResponseData>,
  reject?: RejectFunc
): PromiseAction<CompleteSubmissionPayload, SubmissionResponseData> {
  return {
    type: COMPLETE_SIM_SUBMISSION,
    payload,
    resolve,
    reject
  };
}

export function completeSimSubmissionSuccess(submission: SimSubmission): Action<SimSubmission> {
  return {
    type: COMPLETE_SIM_SUBMISSION_SUCCESS,
    payload: submission
  };
}

export function completeSimSubmissionFailure(
  originalPayload: CompleteSubmissionPayload,
  error: SimManagementError
): ActionMeta<SimManagementError, CompleteSubmissionPayload> {
  return {
    error: true,
    type: COMPLETE_SIM_SUBMISSION_FAILURE,
    payload: error,
    meta: originalPayload
  };
}

export function clearSimSubmission(): Redux.Action {
  return {
    type: CLEAR_SIM_SUBMISSION
  };
}
